import { useState, useMemo, useEffect } from 'react';
import ModalShell from './ModalShell';
import {
  STAGES,
  STAGE_LABELS,
  REJECTION_REASONS,
  NOT_SELECTED_REASONS,
  RATING_OPTIONS,
  ASSESSMENT_RECOMMENDATIONS,
  clientAllowedTargets,
  staffAllowedTargets,
  needsInterviewAssessment
} from '../constants/hiring';
import { emptyOfferDetails, offerDetailsValid } from '../constants/offer';
import OfferDetailsForm from './OfferDetailsForm';
import {
  emptyInterviewSchedule,
  interviewScheduleValid,
  needsInterviewSchedule,
  fromDatetimeLocal
} from '../constants/interview';
import InterviewScheduleForm from './InterviewScheduleForm';
import { suggestStatusNote } from '../utils/stageStatusNotes';
import { isPendingClientSubmission } from '../utils/applicationClientVisibility';

/** Pseudo-action in the stage dropdown (does not change ATS stage). */
export const SUBMIT_TO_CLIENT_ACTION = 'submit_to_client';

const STAGE_COLORS = {
  submitted: 'bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))]',
  selected: 'bg-cyan-100 text-cyan-800',
  interview: 'bg-yellow-100 text-yellow-700',
  shortlisted: 'bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))]',
  offer: 'bg-purple-100 text-purple-700',
  joined: 'bg-green-100 text-green-700',
  relieved: 'bg-orange-100 text-orange-700',
  rejected: 'bg-red-100 text-red-700',
  not_selected: 'bg-rose-100 text-rose-800'
};

const emptyAssessment = () => ({
  recommendation: '',
  technicalRating: '',
  communicationRating: '',
  cultureFitRating: '',
  overallScore: '',
  strengths: '',
  concerns: '',
  summary: ''
});

const STAGE_PLACEHOLDER = '';

export default function StageUpdateModal({
  app,
  userRole,
  onClose,
  onSave,
  onSubmitToClient,
  saving,
  submitToClientSaving = false,
  errorMessage
}) {
  const initialStage = app.stage || 'submitted';
  const [stage, setStage] = useState(STAGE_PLACEHOLDER);
  const [note, setNote] = useState(app.statusNotes?.trim() || '');
  const [noteTouched, setNoteTouched] = useState(Boolean(app.statusNotes?.trim()));

  useEffect(() => {
    if (!stage || stage === initialStage || noteTouched) return;
    setNote(prev => {
      const suggested = suggestStatusNote(stage, app);
      return prev.trim() ? prev : suggested;
    });
  }, [stage, initialStage, app, noteTouched]);
  const [rejectionReasonCode, setRejectionReasonCode] = useState(app.rejectionReasonCode || '');
  const [rejectionComment, setRejectionComment] = useState(app.rejectionComment || '');
  const [notSelectedReasonCode, setNotSelectedReasonCode] = useState(app.notSelectedReasonCode || '');
  const [notSelectedComment, setNotSelectedComment] = useState(app.notSelectedComment || '');
  const [assessment, setAssessment] = useState(emptyAssessment());
  const [offerDetails, setOfferDetails] = useState(emptyOfferDetails());
  const [interviewSchedule, setInterviewSchedule] = useState(emptyInterviewSchedule());
  const [joiningDate, setJoiningDate] = useState('');
  const [appointmentLetterFile, setAppointmentLetterFile] = useState(null);
  const [lastWorkingDay, setLastWorkingDay] = useState('');
  const [exitDocumentFile, setExitDocumentFile] = useState(null);
  const [reliefNote, setReliefNote] = useState('');

  const pendingClientSubmit = isPendingClientSubmission(app);
  const showSubmitToClientOption =
    userRole !== 'client' && initialStage === 'submitted' && pendingClientSubmit;

  const stageOptions = useMemo(() => {
    if (userRole === 'client') {
      const allowed = clientAllowedTargets(initialStage);
      return allowed.length ? allowed : [initialStage];
    }
    const allowed = staffAllowedTargets(initialStage, app);
    const options = allowed.length ? [...allowed] : [initialStage];
    if (showSubmitToClientOption) {
      return [SUBMIT_TO_CLIENT_ACTION, ...options];
    }
    return options;
  }, [userRole, initialStage, app, showSubmitToClientOption]);

  const isSubmitToClient = stage === SUBMIT_TO_CLIENT_ACTION;

  const showAssessment = needsInterviewAssessment(userRole, initialStage, stage, app);
  const showRejection = stage === 'rejected';
  const showNotSelected = stage === 'not_selected';
  const showSelectedNote = stage === 'selected' && initialStage !== 'selected';
  const showOfferDetails = stage === 'offer' && initialStage !== 'offer';
  const showInterviewSchedule = needsInterviewSchedule(initialStage, stage);
  const showJoining = stage === 'joined' && initialStage === 'offer';
  const showRelief = stage === 'relieved' && initialStage === 'joined';
  const replacementDays = app.replacementPeriodDays ?? 90;

  const handleSubmit = () => {
    if (!stage || stage === initialStage) return;

    if (isSubmitToClient) {
      onSubmitToClient?.(note.trim() || 'Submitted for company review');
      return;
    }

    const schedPayload = showInterviewSchedule
      ? {
          ...interviewSchedule,
          scheduledAt: fromDatetimeLocal(
            interviewSchedule.scheduledAtLocal || interviewSchedule.scheduledAt
          ),
          instructions: interviewSchedule.instructions?.trim() || note
        }
      : undefined;

    const statusNote = note.trim();
    const remarksNote = showNotSelected ? notSelectedComment.trim() : '';
    const combinedNote =
      showNotSelected && statusNote && remarksNote && statusNote !== remarksNote
        ? `${remarksNote} · ${statusNote}`
        : statusNote || remarksNote;

    onSave({
      stage,
      note: combinedNote,
      rejectionReasonCode: showRejection ? rejectionReasonCode : undefined,
      rejectionComment: showRejection ? rejectionComment : undefined,
      notSelectedReasonCode: showNotSelected ? notSelectedReasonCode : undefined,
      notSelectedComment: showNotSelected ? notSelectedComment : undefined,
      interviewAssessment: showAssessment ? assessment : undefined,
      offerDetails: showOfferDetails ? offerDetails : undefined,
      interviewSchedule: schedPayload,
      joiningDate: showJoining ? joiningDate : undefined,
      appointmentLetterFile: showJoining ? appointmentLetterFile : undefined,
      lastWorkingDay: showRelief ? lastWorkingDay : undefined,
      exitDocumentFile: showRelief ? exitDocumentFile : undefined,
      reliefNote: showRelief ? reliefNote : undefined
    });
  };

  const assessmentValid =
    !showAssessment ||
    (assessment.recommendation &&
      assessment.summary?.trim() &&
      (stage !== 'rejected' || assessment.recommendation === 'no-hire' || true));

  const notSelectedValid =
    !showNotSelected ||
    (notSelectedReasonCode &&
      (notSelectedComment.trim() || note.trim()) &&
      (notSelectedReasonCode !== 'other' || notSelectedComment.trim()));

  const rejectionValid =
    !showRejection ||
    (rejectionReasonCode &&
      (rejectionReasonCode !== 'other' || rejectionComment.trim()));

  const offerValid = !showOfferDetails || offerDetailsValid(offerDetails);
  const interviewValid =
    !showInterviewSchedule ||
    interviewScheduleValid(interviewSchedule, { requireInstructions: true });

  const joiningValid =
    !showJoining || (joiningDate && appointmentLetterFile);

  const reliefValid = !showRelief || (lastWorkingDay && exitDocumentFile);

  const stageChosen = Boolean(stage) && (stage !== initialStage || isSubmitToClient);

  const canSubmit =
    stageChosen &&
    !isSubmitToClient &&
    assessmentValid &&
    rejectionValid &&
    notSelectedValid &&
    offerValid &&
    interviewValid &&
    joiningValid &&
    reliefValid &&
    (userRole !== 'client' || stageOptions.includes(stage));

  const canSubmitToClient = isSubmitToClient && Boolean(onSubmitToClient);

  const busy = saving || submitToClientSaving;

  const submitHint = useMemo(() => {
    if (canSubmit || canSubmitToClient) return null;
    if (!stage) return 'Select an action from the dropdown.';
    if (isSubmitToClient) return null;
    if (stage === initialStage) return 'Choose a new stage (different from the current stage).';
    if (showInterviewSchedule && !interviewValid) {
      const when = interviewSchedule.scheduledAtLocal || interviewSchedule.scheduledAt;
      if (!when) return 'Set interview date & time.';
      if (interviewSchedule.interviewType === 'virtual' && !interviewSchedule.meetingLink?.trim()) {
        return 'Add a meeting link for virtual interviews.';
      }
      if (interviewSchedule.interviewType === 'in_person' && !interviewSchedule.location?.trim()) {
        return 'Add venue / address for in-person interviews.';
      }
      if (!interviewSchedule.instructions?.trim()) {
        return 'Add instructions & requirements for the candidate.';
      }
    }
    if (showRejection && !rejectionValid) return 'Complete rejection reason and comments.';
    if (showNotSelected && !notSelectedValid) {
      return 'Select a reason and add remarks for Not Selected.';
    }
    if (showOfferDetails && !offerValid) return 'Complete all required offer fields.';
    if (showJoining && !joiningValid) return 'Add joining date and appointment letter.';
    if (showRelief && !reliefValid) return 'Add date of leaving and proof document.';
    if (showAssessment && !assessmentValid) return 'Complete interview assessment (recommendation and summary).';
    return null;
  }, [
    canSubmit,
    canSubmitToClient,
    isSubmitToClient,
    stage,
    initialStage,
    showInterviewSchedule,
    interviewValid,
    interviewSchedule,
    showNotSelected,
    notSelectedValid,
    showRejection,
    rejectionValid,
    showOfferDetails,
    offerValid,
    showJoining,
    joiningValid,
    showRelief,
    reliefValid,
    showAssessment,
    assessmentValid
  ]);

  return (
    <ModalShell
      title={userRole === 'client' ? 'Interview decision' : 'Update application stage'}
      onClose={onClose}
      curvy
      closeOnBackdropClick={false}
      maxWidth="max-w-lg"
      footer={
        <>
          <div className="mr-auto text-left max-w-[min(100%,20rem)]">
            {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
            {submitHint && !errorMessage && (
              <p className="text-xs text-amber-700">{submitHint}</p>
            )}
          </div>
          <button type="button" className="px-4 py-2 text-sm border border-[hsl(var(--ds-border))]" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`px-4 py-2 text-sm font-medium disabled:opacity-60 ${
              isSubmitToClient
                ? 'border border-[hsl(var(--ds-success))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-success))] hover:bg-[hsl(var(--ds-success)/0.08)]'
                : 'border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))]'
            }`}
            disabled={busy || (isSubmitToClient ? !canSubmitToClient : !canSubmit)}
            onClick={handleSubmit}
          >
            {busy
              ? 'Saving...'
              : isSubmitToClient
                ? 'Submit for company review'
                : 'Save'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="text-sm text-[hsl(var(--ds-muted-fg))] bg-[hsl(var(--ds-muted))] p-3 border border-[hsl(var(--ds-border))] space-y-1">
          <div>
            <strong>Application:</strong> {app.id}
          </div>
          <div>
            <strong>Job:</strong> {app.jobTitle || app.jobId}
          </div>
          <div>
            <strong>Candidate:</strong> {app.candidateName || app.candidateId}
          </div>
          <div>
            <strong>Current stage:</strong>{' '}
            <span className={`px-2 py-0.5 text-xs ${STAGE_COLORS[initialStage] || ''}`}>
              {STAGE_LABELS[initialStage] || initialStage}
            </span>
          </div>
        </div>

        {initialStage === 'submitted' && userRole !== 'client' && (
          <p className="text-xs text-cyan-800 bg-cyan-50 border border-cyan-200 px-3 py-2">
            {pendingClientSubmit ? (
              <>
                This submission is <strong>internal only</strong> (not visible to the hiring company). Choose{' '}
                <strong>Submit for company review</strong> below when ready, or <strong>Not Selected</strong> with remarks if
                the candidate should not proceed.
              </>
            ) : (
              <>
                Review the submission: mark as <strong>Selected</strong> if OK to schedule an interview, or{' '}
                <strong>Not Selected</strong> with remarks if the candidate should not proceed.
              </>
            )}
          </p>
        )}

        {isSubmitToClient && (
          <p className="text-xs text-[hsl(var(--ds-success))] bg-[hsl(var(--ds-success)/0.08)] border border-[hsl(var(--ds-success)/0.25)] rounded-xl px-3 py-2">
            The hiring company will be notified and can view this candidate for selection. Stage stays{' '}
            <strong>Submitted</strong> until you mark Selected or Not Selected afterward.
          </p>
        )}

        {initialStage === 'selected' && stage === 'interview' && (
          <p className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 px-3 py-2">
            Candidate is selected — complete the interview schedule below to move to Interview stage.
          </p>
        )}

        {showSelectedNote && (
          <p className="text-xs text-cyan-800 bg-cyan-50 border border-cyan-200 px-3 py-2">
            Selected candidates are approved for interview scheduling. Add optional notes below.
          </p>
        )}

        {userRole === 'client' && initialStage === 'interview' && !app.interviewRoundsComplete && (
          <p className="text-xs text-[hsl(var(--ds-warning-fg))] bg-[hsl(var(--ds-warning)/0.12)] border border-[hsl(var(--ds-warning)/0.35)] px-3 py-2">
            {app.interviewRounds?.length
              ? 'Complete each interview round and feedback in the application detail panel before sending an offer or rejection.'
              : 'Complete the interview assessment below before sending an offer or rejection.'}{' '}
            Recruiter and candidate will be notified by email and in-app notification.
          </p>
        )}
        {userRole === 'client' && initialStage === 'interview' && app.interviewRoundsComplete && (
          <p className="text-xs text-green-800 bg-green-50 border border-green-200 px-3 py-2">
            All interview rounds are complete. Shortlist the candidate or mark as Rejected. Offer is issued only after
            shortlisting.
          </p>
        )}

        {initialStage === 'shortlisted' && stage === 'offer' && (
          <p className="text-xs text-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)] border border-[hsl(var(--ds-primary)/0.25)] px-3 py-2">
            Moving to Offer is limited by open positions on the job. If the offer limit is reached, reject or join other
            candidates first, or increase the job headcount.
          </p>
        )}

        {userRole === 'client' && initialStage === 'offer' && (
          <p className="text-xs text-green-800 bg-green-50 border border-green-200 px-3 py-2">
            Mark as joined only after the candidate has accepted the offer. Upload the appointment letter and
            enter the actual joining date.
          </p>
        )}

        {initialStage === 'joined' && stage === 'relieved' && (
          <p className="text-xs text-orange-800 bg-orange-50 border border-orange-200 px-3 py-2">
            Record exit when the employee leaves: enter the <strong>date of leaving</strong> and upload{' '}
            <strong>proof</strong> (relieving letter, resignation acceptance, etc.). If relieved within the{' '}
            {replacementDays}-day replacement period, the position is reopened automatically for hiring.
          </p>
        )}

        <div className="field-with-label">
          <select
            className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
            value={stage}
            onChange={e => setStage(e.target.value)}
          >
            <option value={STAGE_PLACEHOLDER} disabled>
              Select stage
            </option>
            {stageOptions.map(s => (
              <option key={s} value={s}>
                {s === SUBMIT_TO_CLIENT_ACTION ? 'Submit for company review' : STAGE_LABELS[s] || s}
              </option>
            ))}
          </select>
          <label>{showSubmitToClientOption ? 'Action' : 'New stage'}</label>
        </div>

        {showNotSelected && (
          <div className="space-y-3 border border-rose-100 bg-rose-50/40 p-3">
            <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">Not selected — will not proceed to interview</p>
            <div className="field-with-label">
              <select
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                value={notSelectedReasonCode}
                onChange={e => setNotSelectedReasonCode(e.target.value)}
              >
                <option value="">Select reason</option>
                {NOT_SELECTED_REASONS.map(r => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </select>
              <label>Reason (required)</label>
            </div>
            <div className="field-with-label">
              <textarea
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                rows={3}
                placeholder=" "
                value={notSelectedComment}
                onChange={e => setNotSelectedComment(e.target.value)}
              />
              <label>
                Remarks {notSelectedReasonCode === 'other' ? '(required)' : '(required if status notes empty)'}
              </label>
            </div>
          </div>
        )}

        {showRejection && (
          <div className="space-y-3 border border-red-100 bg-red-50/30 p-3">
            <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">Rejection details</p>
            <div className="field-with-label">
              <select
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                value={rejectionReasonCode}
                onChange={e => setRejectionReasonCode(e.target.value)}
              >
                <option value="">Select reason</option>
                {REJECTION_REASONS.map(r => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </select>
              <label>Reason</label>
            </div>
            <div className="field-with-label">
              <textarea
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                rows={3}
                placeholder=" "
                value={rejectionComment}
                onChange={e => setRejectionComment(e.target.value)}
              />
              <label>Comments {rejectionReasonCode === 'other' ? '(required)' : '(optional)'}</label>
            </div>
          </div>
        )}

        {showInterviewSchedule && (
          <InterviewScheduleForm
            value={interviewSchedule}
            onChange={setInterviewSchedule}
            clientContacts={app.clientContacts || []}
            clientAddresses={app.clientAddresses || []}
          />
        )}

        {showOfferDetails && (
          <>
            <p className="text-xs text-purple-800 bg-purple-50 border border-purple-200 px-3 py-2">
              Enter complete offer terms. The candidate will see these details and can accept, negotiate, or
              decline. All parties receive email and bell notifications.
            </p>
            <OfferDetailsForm value={offerDetails} onChange={setOfferDetails} />
          </>
        )}

        {showJoining && (
          <div className="space-y-3 border border-green-100 bg-green-50/30 p-3">
            <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">Joining details</p>
            <div className="field-with-label">
              <input
                type="date"
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                value={joiningDate}
                onChange={e => setJoiningDate(e.target.value)}
              />
              <label>Actual joining date (required)</label>
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--ds-muted-fg))] block mb-1">
                Appointment letter (required) — PDF or image
              </label>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                className="text-sm w-full"
                onChange={e => setAppointmentLetterFile(e.target.files?.[0] || null)}
              />
              {appointmentLetterFile && (
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">{appointmentLetterFile.name}</p>
              )}
            </div>
          </div>
        )}

        {showRelief && (
          <div className="space-y-3 border border-orange-100 bg-orange-50/30 p-3">
            <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">Relief / exit details</p>
            <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
              Record the employee&apos;s last day and upload proof (relieving letter, resignation acceptance,
              or similar).
            </p>
            <div className="field-with-label">
              <input
                type="date"
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                value={lastWorkingDay}
                onChange={e => setLastWorkingDay(e.target.value)}
              />
              <label>Date of leaving (required)</label>
            </div>
            <div>
              <label className="text-xs text-[hsl(var(--ds-muted-fg))] block mb-1">
                Proof of exit (required) — PDF, Word, or image
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
                className="text-sm w-full"
                onChange={e => setExitDocumentFile(e.target.files?.[0] || null)}
              />
              {exitDocumentFile && (
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">{exitDocumentFile.name}</p>
              )}
            </div>
            <div className="field-with-label">
              <textarea
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                rows={2}
                placeholder=" "
                value={reliefNote}
                onChange={e => setReliefNote(e.target.value)}
              />
              <label>Exit notes (optional)</label>
            </div>
          </div>
        )}

        {showAssessment && (
          <div className="space-y-3 border border-[hsl(var(--ds-primary)/0.2)] bg-[hsl(var(--ds-primary)/0.06)]/30 p-3">
            <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">Interview assessment</p>
            <div className="field-with-label">
              <select
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                value={assessment.recommendation}
                onChange={e => setAssessment(a => ({ ...a, recommendation: e.target.value }))}
              >
                <option value="">Select recommendation</option>
                {ASSESSMENT_RECOMMENDATIONS.map(r => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <label>Recommendation</label>
            </div>
            {['technicalRating', 'communicationRating', 'cultureFitRating'].map(field => (
              <div key={field} className="field-with-label">
                <select
                  className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))] capitalize"
                  value={assessment[field]}
                  onChange={e => setAssessment(a => ({ ...a, [field]: e.target.value }))}
                >
                  <option value="">—</option>
                  {RATING_OPTIONS.filter(Boolean).map(r => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <label>{field.replace('Rating', ' rating')}</label>
              </div>
            ))}
            <div className="field-with-label">
              <input
                type="number"
                min={1}
                max={10}
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                value={assessment.overallScore}
                onChange={e => setAssessment(a => ({ ...a, overallScore: e.target.value }))}
                placeholder=" "
              />
              <label>Overall score (1–10)</label>
            </div>
            <div className="field-with-label">
              <textarea
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                rows={2}
                placeholder=" "
                value={assessment.strengths}
                onChange={e => setAssessment(a => ({ ...a, strengths: e.target.value }))}
              />
              <label>Strengths</label>
            </div>
            <div className="field-with-label">
              <textarea
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                rows={2}
                placeholder=" "
                value={assessment.concerns}
                onChange={e => setAssessment(a => ({ ...a, concerns: e.target.value }))}
              />
              <label>Concerns</label>
            </div>
            <div className="field-with-label">
              <textarea
                className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                rows={3}
                placeholder=" "
                value={assessment.summary}
                onChange={e => setAssessment(a => ({ ...a, summary: e.target.value }))}
              />
              <label>Summary (required)</label>
            </div>
          </div>
        )}

        <div className="field-with-label">
          <textarea
            className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
            rows={2}
            placeholder=" "
            value={note}
            onChange={e => {
              setNoteTouched(true);
              setNote(e.target.value);
            }}
          />
          <label>Status notes</label>
        </div>
      </div>
    </ModalShell>
  );
}
